/** @file One commit of the Activity feed: actor badge, rev, time, commands, targets, tags, Revert. */
import type { CommitSummary } from '@pose2d/api/types';
import { useState } from 'react';
import { history } from '../../api/history/history';
import { useT } from '../../shell/i18n/useT';
import { Button } from '../../ui-kit/Button';
import { actorTone } from './actorTone';
import { commitLine } from './commitLine';

/** Props of ActivityRow; `onActor` (E8.8) turns the badge into the AI-view link. */
export interface ActivityRowProps {
  summary: CommitSummary;
  undone: boolean;
  onActor: ((actor: string) => void) | null;
}

const clock = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const timeOf = (ts: string): string => (Number.isNaN(Date.parse(ts)) ? ts : clock.format(new Date(ts)));

/** The creation commit, repair commits (CR-015, W3-14) and undone rows cannot be reverted, nor can a row mid-request. */
const lockedRevert = (summary: CommitSummary, undone: boolean, busy: boolean): boolean =>
  busy || undone || summary.repair === true || summary.creation === true || summary.rev <= 1;

/** One feed row; the Revert button shows progress while its request runs. */
export function ActivityRow({ summary, undone, onActor }: ActivityRowProps) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const line = commitLine(t, summary);
  const revert = (): void => {
    setBusy(true);
    void history.revert(summary.rev).finally(() => { setBusy(false); });
  };
  const badge = <span className="activity-actor live-pill" data-tone={actorTone(summary.actor)}>{summary.actor}</span>;
  return (
    <li className={undone ? 'activity-row activity-undone' : 'activity-row'}>
      {onActor === null ? badge : (
        <button type="button" className="activity-actor-link" title={t('activity.openAiView', { actor: summary.actor })} onClick={() => { onActor(summary.actor); }}>{badge}</button>
      )}
      <span className="activity-rev">{t('activity.rev', { rev: summary.rev })}</span>
      <time className="activity-time" dateTime={summary.ts}>{timeOf(summary.ts)}</time>
      <span className="activity-commands">{summary.revertOf === null ? line.commands : t('activity.revertOf', { rev: summary.revertOf })}</span>
      {line.targets === '' ? null : <span className="activity-targets">{line.targets}</span>}
      {undone ? <span className="activity-tag">{t('activity.reverted')}</span> : null}
      {summary.repair === true ? <span className="activity-tag">{t('activity.repair')}</span> : null}
      <Button label={busy ? t('activity.reverting') : t('activity.revert')} disabled={lockedRevert(summary, undone, busy)} onClick={revert} />
    </li>
  );
}
